import { Link } from 'react-router-dom'
import { company } from '../data/site'

/**
 * The CASA logo.
 *
 * The real artwork, in two colourways: black lettering for light surfaces and
 * white lettering for dark ones. Both are shipped and **both are always in the
 * DOM** - which one shows is decided in CSS (see `.logo__img` in layout.css),
 * not here.
 *
 * That is deliberate. The correct colourway depends on the surface behind the
 * logo, and that is not the same thing as the colour scheme:
 *
 *   | surface                              | lettering |
 *   | ------------------------------------ | --------- |
 *   | header at the top of a page (over the dark hero band) | white |
 *   | header once scrolled, light scheme (white pane)       | black |
 *   | header once scrolled, dark scheme (dark pane)         | white |
 *   | footer, mobile drawer (navy)                          | white |
 *
 * Picking in JavaScript would need both the theme *and* the scroll state, and
 * would swap the `src` after first paint - a visible flicker on the hero. CSS
 * has both facts already, as `[data-theme]` and the `.site-header--*` class.
 *
 * Two variants, one file each:
 *
 *   - `full` - the complete lockup, including the "BESSER GEMACHT" strapline.
 *     Used in the footer, where there is vertical room for it.
 *   - `compact` - the same image, with the strapline cropped off in CSS
 *     (`.logo--compact` in layout.css shows its top 55%). Used in the header:
 *     76px of header height leaves ~44px for a logo, and at that size the
 *     strapline is about three pixels per character - an illegible smudge.
 *
 * The crop is CSS rather than a second pair of files because the only image tool
 * available here is `sips`, which crops from the centre only and so cannot take
 * the top of an image. The upside is that the header and the footer then share
 * one cached asset instead of downloading four.
 *
 * Both colourways are resized from the originals in src/assets/brand/ (5823px
 * wide, ~270 KB each); the command is in README.md.
 */

/**
 * The chevron mark, kept for ornament only.
 *
 * A redraw of `scraped-site/images/inline-svg/startseite-icon-01.svg` - the one
 * brand asset that survived the old site, because it was inlined in the markup
 * rather than sitting in the missing uploads directory. It reproduces that file's
 * proportions exactly (45° arms, 5.84/100 stroke, 26.6/100 vertical pitch) as
 * three stroked polylines.
 *
 * It is no longer the logo: that is the real artwork above. This stays because
 * it is a single-colour vector and therefore the only mark that can be tinted
 * with `currentColor` and scaled to 420px as the watermark behind the CTA band
 * (see CtaBand.jsx) and the 404 page. The raster logo can do neither.
 */
export function LogoMark({ className, size = 30, ...rest }) {
  const chevrons = [5.8, 32.4, 59]

  return (
    <svg
      className={className}
      width={size}
      height={size * 0.96}
      viewBox="0 0 100 96"
      fill="none"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {chevrons.map((y) => (
        <path
          key={y}
          d={`M19.05 ${y} L50 ${y + 30.95} L80.95 ${y}`}
          stroke="currentColor"
          strokeWidth="5.84"
          strokeLinecap="butt"
          strokeLinejoin="miter"
        />
      ))}
    </svg>
  )
}

const ART = {
  onLight: '/images/logo-on-light.png',
  onDark: '/images/logo-on-dark.png',
  width: 480,
  height: 251,
}

/**
 * @param {object} props
 * @param {boolean} [props.asLink]   Render as a router link to the home page.
 * @param {'compact'|'full'} [props.variant]
 * @param {boolean} [props.eager]    Skip lazy loading. The header logo is in the
 *                                   first viewport on every route, so it should
 *                                   not be deferred.
 */
export default function Logo({
  asLink = true,
  variant = 'compact',
  className = '',
  eager = true,
}) {
  /*
   * `alt=""` on both: the link that wraps them carries the accessible name, and
   * a screen reader would otherwise announce the company twice - once per
   * colourway, since both images are in the DOM.
   */
  const content = (
    <>
      <img
        className="logo__img logo__img--on-dark"
        src={ART.onDark}
        width={ART.width}
        height={ART.height}
        alt=""
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        draggable="false"
      />
      <img
        className="logo__img logo__img--on-light"
        src={ART.onLight}
        width={ART.width}
        height={ART.height}
        alt=""
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        draggable="false"
      />
    </>
  )

  const classes = `logo logo--${variant} ${className}`.trim()

  if (!asLink) {
    return (
      <span className={classes} role="img" aria-label={company.brand}>
        {content}
      </span>
    )
  }

  return (
    <Link
      to="/"
      className={classes}
      aria-label={`${company.brand} - zur Startseite`}
    >
      {content}
    </Link>
  )
}
